import { Accordion, AccordionDetails, AccordionSummary, Checkbox, MenuItem, MenuList } from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CustomIconButton, CustomTooltip, icon, CustomButton, DropDown, IconComponent } from "..";
import { setCrossFilters } from "../../slice";
import { NavUtils } from "../../../navigation";
import { generateGreyShade } from "../../../views/admin-categories/utils/utils";
import styles from "./ExpandableCategoryRow.module.css";
import { isSegFeatureEnabled, SegFlags, translate, translateDate } from "../../providers";
import CategoryContents from "./CategoryContents";
import { isNonEmptyArray } from "../../utils";

const getRedirectState = (catId, documentIds) => ({
	state: {
		filters: {
			categories: {
				matchAll: false,
				is: true,
				CATEGORY: [catId],
				FAVORITE: [],
			},
			isFilterTocOut: false,
			documents: {
				is: true,
				matchAll: true,
				elements: documentIds,
			},
			separator: "AND",
			Type: "InformationSearchFilterDTO",
		},
	},
});

const getChildrensCounts = (category, favCategories, counters = { selected: 0, total: 0 }) => {
	let tempCounters = { ...counters };
	if (!Array.isArray(category.subThematic) || category.subThematic.length === 0) {
		return counters;
	}
	category.subThematic.forEach((cat) => {
		tempCounters.total++;
		if (favCategories?.some((favCat) => favCat.id === cat.id)) {
			tempCounters.selected += 1;
		}
		tempCounters = getChildrensCounts(cat, favCategories, tempCounters);
	});
	return tempCounters;
};

export default function ExpandableCategoryRow({
	activeCategorizations = [],
	isAiVersion = false,
	category,
	className = "",
	disableCheckboxes = false,
	expanded = false,
	indentLevel = 0,
	isAdminCategories = false,
	isEditable = false,
	isManaging = false,
	isRowChecked,
	isSubscription = false,
	onCheck,
	onClickAdd,
	onClickAddAction,
	onClickDelete,
	onClickEdit,
	onOpenUsersList,
	onSelectAiCategorization = null,
	onSubscribe,
	onToggleExpand,
	onUnsubscribe,
	projectId,
}) {
	const [openMoreDropDown, setOpenMoreDropDown] = useState(false);
	const favoriteCategories = useSelector(({ context }) => context.favoriteCategories.categories);
	const hasAvailableAIQuota = useSelector(({ context }) => context?.hasAvailableAIQuota);
	const anchorRef = useRef(null);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isSubscribed = useMemo(() => {
		if (!isSubscription) {
			return false;
		}
		return favoriteCategories.some((favCategory) => favCategory.id === category.id);
	}, [isSubscription, favoriteCategories, category.id]);
	const categoryCounter = useMemo(
		() => getChildrensCounts(category, favoriteCategories),
		[category, favoriteCategories]
	);
	const agentStatus = useMemo(() => {
		if (category.agentStatus === "STARTED" || activeCategorizations.includes(category.id)) {
			return (
				<span className={styles.row__inProgress}>{translate("admin-categories.category-row.in-progress")}</span>
			);
		}
		if (category.agentStatus === "FINISHED") {
			return (
				<span className={styles.row__finished}>
					<span>{translate("admin-categories.category-row.finished")}</span>
					<span>{translateDate(category.agentLastModified)}</span>
				</span>
			);
		}
		if (category.agentStatus === "FAILED") {
			return (
				<span className={styles.row__finished}>
					<span>{translate("admin-categories.category-row.failed")}</span>
					<span>{translateDate(category.agentLastModified)}</span>
				</span>
			);
		}
		return <span>-</span>;
	}, [category, activeCategorizations]);
	let allowExpand = true;
	const handleChange = () => {
		setTimeout(() => {
			if (allowExpand && (category.childrenCount > 0 || category.subThematic?.length > 0)) {
				onToggleExpand(category);
			} else {
				allowExpand = true;
			}
		}, 0);
	};
	const preventExpand = () => {
		allowExpand = false;
	};
	const handleCloseDropDown = () => {
		setOpenMoreDropDown(false);
	};
	const handleClickAdd = () => {
		preventExpand();
		onClickAdd(category.id);
		handleCloseDropDown();
	};
	const handleClickEdit = () => {
		preventExpand();
		onClickEdit(category);
		handleCloseDropDown();
	};
	const handleSelectAiCategorization = () => {
		preventExpand();
		onSelectAiCategorization(category);
		handleCloseDropDown();
	};
	const handleClickDelete = () => {
		preventExpand();
		onClickDelete(category);
		handleCloseDropDown();
	};
	const handleClickAddContent = () => {
		preventExpand();
		if (isSegFeatureEnabled(SegFlags.SMART_SEARCH)) {
			navigate(NavUtils.goToMySearch(projectId));
		}
	};
	const handleCheck = (event) => {
		preventExpand();
		onCheck(category, event.target.checked);
	};
	const handleClickCounts = () => {
		preventExpand();
		dispatch(
			setCrossFilters({
				categories: {
					elements: [category.id],
					is: true,
					matchAll: false,
				},
			})
		);
		if (isSegFeatureEnabled(SegFlags.SMART_SEARCH)) {
			navigate(NavUtils.goToMySearch(projectId), getRedirectState(category.id, category.documentIds));
		}
	};
	const handleOpenUsersList = () => {
		preventExpand();
		onOpenUsersList(category);
	};
	const handleSubscribtion = (e) => {
		if (isSubscribed) {
			onUnsubscribe(e, category);
		} else {
			onSubscribe(e, category);
		}
	};
	const handleOpenDropDown = (e) => {
		e.stopPropagation();
		setOpenMoreDropDown(true);
	};

	const hasSubCategories = isNonEmptyArray(category.subThematic) || category.childrenCount > 0;
	const disabledTextColor = generateGreyShade(180, indentLevel);
	return (
		<Accordion
			classes={{
				root: styles.accordion__root,
			}}
			className={className}
			expanded={expanded}
			style={{
				marginLeft: indentLevel > 0 ? 32 : 0,
			}}
			onChange={handleChange}
		>
			<AccordionSummary
				classes={{
					root: styles.accordionSummary__root,
					content: styles.accordionSummary__content,
				}}
				style={{
					cursor: hasSubCategories ? "pointer" : "default",
				}}
			>
				<div className={styles.row__leftItems}>
					<div className={styles.expandButtonContainer}>
						{hasSubCategories && (
							<CustomIconButton icon={expanded ? icon.faChevronDown : icon.faChevronRight} />
						)}
					</div>
					{!disableCheckboxes && isRowChecked && (
						<Checkbox checked={isRowChecked(category)} color="primary" onChange={handleCheck} />
					)}
					<CustomTooltip showWhenOverflow title={category.name}>
						<div
							className={`${styles.row__name} ${
								isSubscription && !hasSubCategories && styles["row__name--regular"]
							}`}
							data-testid={`cat.tree.${category.id}`}
							style={{
								width: `${250 - 32 * indentLevel}px`,
								color:
									category.filtered === undefined || category.filtered
										? "var(--color-black)"
										: disabledTextColor,
							}}
						>
							{category.name}&nbsp;
							{hasSubCategories && isManaging && (
								<>
									({categoryCounter.selected}/{categoryCounter.total})
								</>
							)}
							{category.generatedByAI && (
								<span className={styles.row__generatedByAi}>
									<IconComponent color="var(--blue-500)" icon={icon.faWandMagicSparkles} size="2xs" />
								</span>
							)}
						</div>
					</CustomTooltip>
				</div>
				<div className={styles.row__rightItems} data-issubscription={isSubscription}>
					{(!isSubscription && (
						<>
							<div
								className={styles.rightItems__description}
								style={{
									color:
										category.filtered === undefined || category.filtered
											? "var(--color-black)"
											: disabledTextColor,
								}}
							>
								{category.description}
							</div>
							{isAdminCategories && isSegFeatureEnabled(SegFlags.FAVORITE_CATEGORIES) && (
								<div className={styles.rightItems__subscriptions}>
									<CustomButton onClick={handleOpenUsersList}>
										<CustomTooltip
											showWhenOverflow
											title={translate("common:expandable-category-row.subscribed-employees", {
												userCount: category.usersSubscribed,
											})}
										>
											<div className={styles.subscriptionsButton__text}>
												{translate("common:expandable-category-row.subscribed-employees", {
													userCount: category.usersSubscribed,
												})}
											</div>
										</CustomTooltip>
									</CustomButton>
								</div>
							)}
							<div className={styles.counterContainer} data-testid={`cat.tree.${category.id}.status`}>
								{translate((category.actionCount > 0 && "common:btn.yes") || "common:btn.no")}
							</div>
							{isAiVersion && <div className={styles.rightItems__actionTitle}>{agentStatus}</div>}
							{!!projectId && (
								<div
									className={styles.rightItems__contents}
									data-testid={`cat.tree.${category.id}.content`}
								>
									<CategoryContents
										contentsCount={category.contentsCount}
										documentsCount={category.documentsCount}
										hierarchyLevel={indentLevel}
										onClickAddContent={handleClickAddContent}
										onClickCounts={handleClickCounts}
									/>
								</div>
							)}
							{isEditable && (
								<div
									className={styles.rightItems__buttonsGroup}
									data-has-unified-button={isAiVersion}
									data-testid={`cat.tree.${category.id}.btn`}
								>
									{(!isAiVersion && (
										<>
											<div className={styles.buttonsGroup__plusButtonContainer}>
												{indentLevel < 4 && (
													<CustomIconButton
														data-testid={`addSubCategory_${category.id}`}
														icon={icon.faPlus}
														size="sm"
														tooltip={translate("common:expandable-category-row.add")}
														variant="outlined"
														onClick={handleClickAdd}
													/>
												)}
											</div>
											<CustomIconButton
												data-testid={`editCategory_${category.id}`}
												icon={icon.faPenToSquare}
												size="sm"
												tooltip={translate("common:expandable-category-row.edit")}
												variant="outlined"
												onClick={handleClickEdit}
											/>
											<CustomIconButton
												data-testid={`deleteCategory_${category.id}`}
												icon={icon.faTrash}
												size="sm"
												tooltip={translate("common:expandable-category-row.delete")}
												variant="outlined"
												onClick={handleClickDelete}
											/>
										</>
									)) || (
										<CustomIconButton
											ref={anchorRef}
											icon={icon.faEllipsisH}
											size="sm"
											variant="outlined"
											onClick={handleOpenDropDown}
										/>
									)}
								</div>
							)}
						</>
					)) || (
						<CustomButton
							color={isSubscribed ? "primary" : "secondary"}
							variant={isSubscribed ? "contained" : "outlined"}
							onClick={handleSubscribtion}
						>
							{isSubscribed
								? translate("common:expandable-category-row.subscribed")
								: translate("common:expandable-category-row.subscribe")}
						</CustomButton>
					)}
					<DropDown
						ref={anchorRef}
						open={openMoreDropDown}
						position="bottom-end"
						onClose={handleCloseDropDown}
						onMouseLeave={handleCloseDropDown}
					>
						<MenuList className={styles.category__moreMenu}>
							{indentLevel < 4 && (
								<MenuItem className={styles["category__moreMenu--menu-item"]} onClick={handleClickAdd}>
									<IconComponent color="var(--grey-500)" icon={icon.faPlus} size="2xs" />
									{translate("common:expandable-category-row.add")}
								</MenuItem>
							)}
							<MenuItem className={styles["category__moreMenu--menu-item"]} onClick={handleClickEdit}>
								<IconComponent color="var(--grey-500)" icon={icon.faPenToSquare} size="xs" />
								{translate("common:expandable-category-row.edit")}
							</MenuItem>
							{(isSegFeatureEnabled(SegFlags.AI_CONTENT_CATEGORIZER) || !projectId) && (
								<CustomTooltip
									title={
										(!hasAvailableAIQuota &&
											translate("common:company.quotas.consumption-reached.tooltip")) ||
										""
									}
								>
									<span>
										<MenuItem
											className={styles["category__moreMenu--menu-item"]}
											disabled={
												category.agentStatus === "STARTED" ||
												activeCategorizations.includes(category.id) ||
												!hasAvailableAIQuota
											}
											onClick={handleSelectAiCategorization}
										>
											<IconComponent color="var(--grey-500)" icon={icon.faTag} size="xs" />
											{translate("common:expandable-category-row.ai-categorize")}
											<IconComponent
												color="var(--blue-500)"
												icon={icon.faWandMagicSparkles}
												size="xs"
											/>
										</MenuItem>
									</span>
								</CustomTooltip>
							)}
							<MenuItem className={styles["category__moreMenu--menu-item"]} onClick={handleClickDelete}>
								<IconComponent color="var(--grey-500)" icon={icon.faTrash} size="xs" />
								{translate("common:expandable-category-row.delete")}
							</MenuItem>
						</MenuList>
					</DropDown>
				</div>
			</AccordionSummary>
			{hasSubCategories && (
				<AccordionDetails
					classes={{
						root: styles.accordionDetails__root,
					}}
				>
					{Array.isArray(category.subThematic) &&
						category.subThematic.map((subCategory) => (
							<ExpandableCategoryRow
								key={subCategory.id}
								activeCategorizations={activeCategorizations}
								category={subCategory}
								disableCheckboxes={disableCheckboxes}
								expanded={
									(subCategory.filtered === false && subCategory.expanded === undefined) ||
									subCategory.expanded
								}
								indentLevel={indentLevel + 1}
								isAdminCategories={isAdminCategories}
								isAiVersion={isAiVersion}
								isEditable={isEditable}
								isRowChecked={isRowChecked}
								isSubscription={isSubscription}
								projectId={projectId}
								onCheck={onCheck}
								onClickAdd={onClickAdd}
								onClickAddAction={onClickAddAction}
								onClickAddContent={handleClickAddContent}
								onClickDelete={onClickDelete}
								onClickEdit={onClickEdit}
								onOpenUsersList={onOpenUsersList}
								onSelectAiCategorization={onSelectAiCategorization}
								onSubscribe={onSubscribe}
								onToggleExpand={onToggleExpand}
								onUnsubscribe={onUnsubscribe}
							/>
						))}
				</AccordionDetails>
			)}
		</Accordion>
	);
}
